/**
 * The transfer queue's vocabulary and states — what is left of the transfer queue's decision half
 * (bata-platform ADR 6031, `docs/adr/6003`) since the one run retired it.
 *
 * **Retired (bata-reqs ADR 9002 stage 3, ruling 7; platform ADR 9049).** The release that deploys the
 * one run retired `POST /inseason/transfers/dispositions` with no shadow phase: it answers 410
 * `planning.compacting.route_retired`. Every store-to-store move is a line of the one run's merged
 * list, decided at `POST /compacting/moves/dispositions` (`./compacting/move-list.tsx`). The decision bar
 * each action type carried on the transfer page is gone with it; the legacy book's transfers keep
 * their history and are read, never decided.
 *
 * What stays: the two vocabularies the in-season queues still read (the reasons are the one approval
 * process's too), and the words for a transfer's recorded decision.
 */

import { useApiQuery } from '@/api';
import {
  dispositionVocabulary as dispositionVocabularyOperation,
  transferReasonVocabulary as transferReasonVocabularyOperation,
  type Disposition,
  type TransferReasonOption,
} from '@/api/generated';

import type { TransferRow } from './api';

/** The reason vocabulary, read from the platform. */
export function useTransferReasonVocabulary() {
  return useApiQuery(transferReasonVocabularyOperation, {});
}

/** The disposition vocabulary — the same query the markdown queue reads, served from one cache. */
export function useDispositionVocabulary() {
  return useApiQuery(dispositionVocabularyOperation, {});
}

export type TransferReasonVocabularyQuery = ReturnType<typeof useTransferReasonVocabulary>;
export type DispositionVocabularyQuery = ReturnType<typeof useDispositionVocabulary>;

/** The transfers of a set no disposition names yet — `null` on the read is pending. */
export function pendingTransferIds(rows: readonly TransferRow[]): readonly string[] {
  return rows.filter((row) => row.disposition === null).map((row) => row.transferId);
}

/** The reasons a disposition may honestly carry. Pure, so the test asserts the filter. */
export function transferReasonsFor(
  options: readonly TransferReasonOption[],
  disposition: Disposition | null,
): readonly TransferReasonOption[] {
  if (disposition === null) return [];
  return options.filter((option) => option.dispositions.includes(disposition));
}

/**
 * Where one transfer stands, in the grid's words. Spelled out so the catalogue check sees
 * every key. `approve` says "not sent" in the same breath, because nothing was.
 */
const STATE_KEY = {
  pending: 'inseason.transferDecision.state.pending',
  approve: 'inseason.transferDecision.state.approve',
  reject: 'inseason.transferDecision.state.reject',
  defer: 'inseason.transferDecision.state.defer',
  adjust: 'inseason.transferDecision.state.adjust',
  // A newer run took the undecided line over (bata-platform ADR 6048): never decidable.
  superseded: 'inseason.transferDecision.state.superseded',
  unknown: 'inseason.transferDecision.state.unknown',
} as const;

/** The key for a transfer's disposition. `null` is pending — never a default decision. */
export function dispositionStateKey(disposition: string | null): string {
  if (disposition === null) return STATE_KEY.pending;
  return (STATE_KEY as Readonly<Record<string, string>>)[disposition] ?? STATE_KEY.unknown;
}
